import { FseMS, FseMEF, FseUE, FseDGT, FseIT, FseOperatorsMS, FseOperatorsDGT, FseOperatorsMEF } from "@/icons";
import { Container } from "@/components/ui/Container";
import { Divider } from "@/components/ui/Divider";
import "./Footer.scss";
import clsx from "clsx";
import { SettingFooterLink } from "@anoki/fse-common/dist";
import { WithVariant } from "@/types/variant";

type FooterProps = {
  footerLinks?: SettingFooterLink[];
  version?: string;
  asLink?: "a" | React.ComponentType<any>;
  className?: string;
  additionalLogo?: string;
  additionalLogoName?: string;
  settingsFooterLinks?: {
    FseMS?: string;
    FseMEF?: string;
    FseIT?: string;
    FseDGT?: string;
    FseUE?: string;
    externalLink?: string;
  };
};

const isExternalLink = (url: string): boolean => {
  if (!url) return false;
  try {
    const linkUrl = new URL(url);

    if (typeof window === "undefined") {
      // In SSR, assume it's internal or return a default
      return true;
    }
    const currentUrl = new URL(window.location.href);
    return linkUrl.hostname !== currentUrl.hostname;
  } catch {
    // If URL parsing fails, assume it's an internal link
    return true;
  }
};

export const Footer = ({
  footerLinks,
  version,
  asLink = "a",
  className,
  additionalLogo,
  additionalLogoName = "Logo aggiuntivo",
  settingsFooterLinks = {
    FseMS: "",
    FseIT: "",
    FseMEF: "",
    FseDGT: "",
    FseUE: "",
    externalLink: "",
  },
  variant = "default",
}: WithVariant<FooterProps>) => {
  const LinkComponent = asLink;

  const INSTITUTION_LOGOS_MAP = {
    default: {
      FseUE,
      FseIT,
      FseMS,
      FseMEF,
      FseDGT
    },
    operator: {
      FseUE,
      FseIT,
      FseOperatorsMS,
      FseOperatorsMEF,
      FseOperatorsDGT
    }
  };

  const INSTITUTION_NAMES = {
    default: {
      FseUE: "Unione Europea",
      FseIT: "Repubblica Italiana",
      FseMS: "Ministero della Salute",
      FseMEF: "Ministero dell'Economia e delle Finanze",
      FseDGT: "Agenzia per l'Italia Digitale",
    },
    operator: {
      FseUE: "Unione Europea",
      FseIT: "Repubblica Italiana",
      FseOperatorsMS: "Ministero della Salute",
      FseOperatorsMEF: "Ministero dell'Economia e delle Finanze",
      FseOperatorsDGT: "Agenzia per l'Italia Digitale",
    }
  } as const;

  const orderedInstitutions = Object.entries(settingsFooterLinks)
    .filter(([key]) => key !== "externalLink")
    .map(([key]) => ({
      key: key as keyof typeof INSTITUTION_LOGOS_MAP[typeof variant],
      Logo: INSTITUTION_LOGOS_MAP[variant][key as keyof typeof INSTITUTION_LOGOS_MAP[typeof variant]],
      name: INSTITUTION_NAMES[variant][key as keyof typeof INSTITUTION_NAMES[typeof variant]],
      href: settingsFooterLinks[key as keyof typeof settingsFooterLinks] || "#",
    }));

  return (
    <footer className={clsx(variant === "operator" ? "bg-white ui-footer-operator" : "bg-slate-20", "ui-footer", className)}>
      <Container>
        <section className="ui-footer-institution-logos">
          {orderedInstitutions.map(({ key, Logo, name, href }, _i) => {
            const isExternal = isExternalLink(href);
            return (
              <LinkComponent
                className="ui-footer-logo-link"
                href={href}
                key={[key, _i].join("-")}
                target={isExternal ? "_blank" : "_self"}
                rel={isExternal ? "noopener noreferrer" : undefined}
                aria-label={`Visita il sito di ${name}${isExternal ? " (si apre in una nuova finestra)" : ""}`}
              >
                <Logo className="ui-footer-logo" decorative={true} variant={variant} />
              </LinkComponent>
            );
          })}
          {additionalLogo && (
            <LinkComponent
              className="ui-footer-logo-link"
              href={settingsFooterLinks?.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Visita il sito di ${additionalLogoName} (si apre in una nuova finestra)`}
            >
              <img
                src={additionalLogo}
                alt=""
                className="ui-footer-logo"
                role="presentation"
              />
            </LinkComponent>
          )}
        </section>
        <Divider bg={variant === "operator" ? "border-divider" : "white"} />
        <section className="ui-footer-links-container">
          <ul className="ui-footer-links-list" role="list">
            {footerLinks?.map((link, i) => {
              if (!link.visible) return null;
              return (
                <li key={i}>
                  <LinkComponent
                    href={link.href}
                    className={clsx(variant === "operator" ? "text-slate-28" : "text-white", "fs-6 fw-semibold text-decoration-underline")}
                    target={link.externalLink ? "_blank" : undefined}
                    rel={link.externalLink ? "noopener noreferrer" : undefined}
                  >
                    {link.label}
                  </LinkComponent>
                </li>
              );
            })}
          </ul>
          <span className={clsx(variant === "operator" ? "text-slate-28" : "text-white", `fw-normal ui-footer-version`)}>
            {version}
          </span>
        </section>
      </Container>
    </footer>
  );
};
